import { useMemo, useState } from 'react';
import { Operation } from '@/types/general';
import { ManageForm } from '@components/forms/ManageForm';
import { useBcCustomersQuery, useCompanyMutation, useCompanyQuery } from '@features/redux/apis/org';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    List,
    ListItem,
    ListItemText,
    Typography,
    Paper,
} from '@mui/material';
import type { RootState } from '@features/redux/store';
import { FormRequiredText } from '@components/forms/text/base/FormRequiredText';
import { Validate } from '@utils/validate';
import { CompanyEffects } from './CompanyEffects';
import { Query } from '@features/redux/apis/utils';
import { BILLING_OPTIONS, DEFAULT_BILLING_OPTIONS, WARRANTY_OPTIONS } from '@consts/company';
import { FormDisabledText } from '@components/forms/text/base/FormDisabledText';
import { FormSelect, type Option } from '@components/forms/dropdowns/base/FormSelect';
import { ConfirmModal } from '@components/dialogs/ConfirmModal';
import { AuditTimeline } from '@components/AuditTrail/AuditTimeline';

import type { ManageProps } from '../Panel';

// ? Don't use withDraft unless we need to. We don't need to
// ? create a draft in the ResellerEffects component, for example.
const useCompanies = Query.withDraft(useCompanyQuery, {
    Reseller: false,
    CompanyName: '',
    Inactive: false,
    CustomerID: null,
    SalesRepTrackingEnabled: true,
    ...DEFAULT_BILLING_OPTIONS,
});

export const Company = ({ operation, id: companyId, onSuccess }: ManageProps) => {
    const { role } = useSelector(({ auth }: RootState) => auth.Roles);
    const auditPk = companyId ? `Company#${companyId}` : null;

    const [open, setOpen] = useState(false);

    const { data: customers, isFetching: customersLoading } = useBcCustomersQuery({});
    const customerMap = useMemo(
        () =>
            customers
                ? customers.reduce((acc, { label, value }) => {
                      acc[value] ??= label;
                      return acc;
                  }, {} as Record<string, any>)
                : {},
        [customers]
    );

    const {
        draft,
        data,
        isFetching: queryLoading,
    } = useCompanies(
        { companyId: +companyId! },
        { skip: operation === Operation.Create, refetchOnMountOrArgChange: true }
    );
    const [draftState, setDraftState] = draft;

    const [mutate, { isLoading: mutationLoading }] = useCompanyMutation({
        // ? We don't want to use the mutation cache if we're creating a new company
        fixedCacheKey: operation === Operation.Edit ? 'CompanyMutation' : 'CompanyCreation',
    });

    const handleSubmit = async () => {
        await mutate({
            method: operation === Operation.Create ? 'POST' : 'PATCH',
            company: draftState,
        });

        onSuccess?.();
    };

    const isLoading = queryLoading || customersLoading || mutationLoading;
    const canEditPrices = role.prices.edit && role.companies.fullAccess;
    const reassigningCustomer = operation === Operation.Edit && draftState.CustomerID !== data?.CustomerID;

    return (
        <>
            <CompanyEffects />

            <ManageForm
                title={operation === Operation.Create ? 'Create New Company' : 'Manage Company'}
                loading={isLoading}
                size={12}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Paper elevation={0} sx={{ width: '100%', padding: 1, border: '1px solid #e0e0e0' }}>
                        <Typography fontSize={'18px'} pt={1} pl={1} pb={1} fontWeight={1000} color={'black'}>
                            Company Info
                        </Typography>

                        <Grid container spacing={2} p={1}>
                            <>
                                <FormDisabledText
                                    size={12}
                                    label='Company ID'
                                    value={data?.CompanyID?.toString() || 'N/A'}
                                />
                            </>

                            <FormRequiredText
                                size={12}
                                stateKey='CompanyName'
                                label='Company Name'
                                disabled={operation === Operation.View}
                                draft={draft}
                                validateFn={Validate.name}
                                helperText={{
                                    valid: '',
                                    invalid: 'Please provide a valid company name',
                                }}
                            />

                            {role.isAdmin && (
                                <FormSelect
                                    defaultFirst={false}
                                    disabled={operation !== Operation.Create && !role.prices.edit}
                                    size={12}
                                    label='Customer Name (Business Central)'
                                    draft={draft}
                                    required
                                    stateKey='CustomerID'
                                    options={(customers as unknown as Option[]) ?? []}
                                />
                            )}

                            {operation !== Operation.View && (
                                <Box margin={'20px'}>
                                    <FormControlLabel
                                        sx={{ userSelect: 'none' }}
                                        checked={Boolean(draftState?.SalesRepTrackingEnabled)}
                                        onChange={({ target }) => {
                                            const checked = (target as HTMLInputElement).checked;
                                            setDraftState((prev) => ({ ...prev, SalesRepTrackingEnabled: checked }));
                                            // setSalesRepTracking(checked);

                                            // ? if not checked, set sales rep tracking state enabled to null
                                            if (!checked) {
                                                setDraftState((prev) => ({ ...prev, SalesRepTrackingEnabled: null }));
                                            }
                                        }}
                                        control={<Checkbox />}
                                        label='Sales Rep Tracking Enabled'
                                    />
                                </Box>
                            )}

                            <Grid item xs={12} sm={12} md={12} display='flex' gap='10px'>
                                {role.isAdmin && role.stores.edit && (
                                    <Button
                                        type='submit'
                                        disabled={!Validate.Org.company(draftState)}
                                        size={'large'}
                                        variant={'contained'}
                                        color={'orange'}
                                        onClick={() => {
                                            if (reassigningCustomer) {
                                                setOpen(true);
                                                return;
                                            }

                                            handleSubmit();
                                        }}>
                                        {operation === Operation.Create ? 'Create' : 'Update'}
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </Paper>

                    {auditPk && operation !== Operation.Create && role.isAdmin && (
                        <Paper elevation={0} sx={{ width: '100%', padding: 1, border: '1px solid #e0e0e0', mt: 2 }}>
                            <Typography fontSize={'18px'} pt={1} pl={1} pb={1} fontWeight={1000} color={'black'}>
                                Audit Log
                            </Typography>
                            <AuditTimeline entityPk={auditPk} />
                        </Paper>
                    )}
                </Grid>
            </ManageForm>

            <ConfirmModal title='Company Correlation' open={open} setOpen={setOpen} onConfirm={handleSubmit}>
                <Typography>Are you sure you want to associate the following companies?</Typography>

                <List>
                    <ListItem>
                        <ListItemText
                            primary={`Company #${data?.CompanyID} - ${data?.CompanyName}`}
                            secondary='RapidCut'
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemText
                            primary={`Company #${draftState?.CustomerID} - ${
                                customerMap?.[draftState?.CustomerID as string]
                            }`}
                            secondary='Business Central'
                        />
                    </ListItem>
                </List>
            </ConfirmModal>
        </>
    );
};
